import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BRAND, FOOD, SCENE } from "@/data/media";
import { SITE, ARCH_LABELS } from "@/data/site";
import { Reveal, LineReveal } from "@/components/motion";

function Opening() {
  return (
    <section className="bg-ink text-cream pt-28 md:pt-36 pb-12 md:pb-16" data-testid="story-opening">
      <div className="u-container">
        {/* Letterhead Header Section */}
        <div className="mb-8 md:mb-10">
          <Reveal>
            <p className="u-eyebrow text-terracotta mb-4">Our Story</p>
          </Reveal>
          <LineReveal
            trigger="load"
            lines={["Two worlds", "One home"]}
            lineClassName="font-display text-4xl md:text-6xl leading-[0.82]"
          />
        </div>

        {/* Content Section (Text & Image side-by-side) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <Reveal delay={0.25}>
              <div className="space-y-6 font-body font-light text-cream/75 text-lg leading-relaxed max-w-2xl">
                <p>
                  Every home begins with a story. For Jomon, that story began in Kerala — where food was never simply food. It was the aroma of spices in the kitchen, the warmth of a meal shared with family, and recipes passed down not through measurements, but through memory.
                </p>
                <p>
                  Years later, that memory travelled with him to England. At 197 High Street, an old timber-framed building, rich with its own history, became the setting for something new — a place where the flavours, traditions and memories of Kerala could find a new home.
                </p>
                <p>
                  And so VEEDU was born — a meeting of Kerala and England, of an old house and a new dream, of heritage and hospitality. Here, the past is not left behind; it is brought to the table, one recipe, one story and one shared meal at a time.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal delay={0.4}>
              <div className="relative overflow-hidden rounded-xl border border-gold/30 bg-cream p-4 shadow-2xl group">
                <img
                  src="/images/Veedu.png"
                  alt="Architectural illustration of #VEEDU house"
                  className="w-full h-auto object-contain transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function ManBehind() {
  return (
    <section className="bg-brown text-cream overflow-hidden" data-testid="story-chef">
      {/* Header row */}
      <div className="u-container pt-16 md:pt-24 pb-8 md:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="u-eyebrow text-gold mb-4">The man behind the house</p>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">Chef Jomon</h2>
              <p className="font-script text-gold text-2xl mt-4">{SITE.chef.role}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <p className="font-body font-light text-cream/70 text-lg leading-relaxed max-w-xl">
                Chef Jomon is the founder and culinary director behind #VEEDU. His cooking is rooted in memory, place and curiosity — shaped by the coastlines, markets and family kitchens of Kerala.
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Full-width editorial image + text split */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Image panel */}
        <Reveal className="relative overflow-hidden" style={{ minHeight: "520px" }}>
          <img
            src="/images/jomon.jpg"
            alt="Chef Jomon"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          {/* subtle dark vignette on right edge for text blending */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-brown/80 hidden lg:block" />
          {/* Bottom gold bar */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-brown/90 to-transparent">
            <span className="u-eyebrow text-gold/80">Director of Culinary · #VEEDU</span>
          </div>
        </Reveal>

        {/* Text panel */}
        <div className="bg-brown/60 lg:bg-transparent flex items-center px-8 md:px-14 py-16 lg:py-20 lg:pl-16">
          <Reveal delay={0.2}>
            <div className="space-y-7 max-w-xl">
              <span className="block w-12 h-px bg-gold" />
              <p className="font-body font-light text-cream/75 text-base md:text-lg leading-relaxed">
                The food begins with Kerala — its coastlines, markets, family kitchens, spices, coconuts, seafood,
                fire and slow cooking — but it does not stop there. His philosophy is to let Kerala converse with the
                wider Indian culinary landscape.
              </p>
              <blockquote className="border-l-2 border-gold pl-6">
                <p className="font-display italic text-2xl md:text-3xl text-cream leading-snug">
                  "South meets North. Coast meets inland. Tradition meets contemporary technique."
                </p>
              </blockquote>
              <p className="font-body font-light text-cream/60 text-base leading-relaxed">
                The result is food that feels familiar yet unexpected — rooted in memory, served with intention.
              </p>
              <p className="font-script text-gold text-xl">Memory meets modern hospitality.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhyVeedu() {
  const lines = [
    "Home is memory.",
    "Home is the smell of spices in a kitchen.",
    "Home is a family gathered around a table.",
    "Home is the sound of food being prepared.",
    "Home is a recipe passed from one generation to another.",
  ];
  return (
    <section className="bg-ink text-goldlight py-12 md:py-16" data-testid="story-why">
      <div className="u-container">
        <Reveal>
          <p className="u-eyebrow text-terracotta mb-4">Why #VEEDU?</p>
          <h2 className="font-display text-4xl md:text-7xl leading-[1.0] max-w-4xl mb-8">
            The name #VEEDU means <span className="italic text-gold">home</span>. But home is not simply a building.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4 max-w-4xl">
          {lines.map((l, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className="font-display text-2xl md:text-3xl text-cream/85 border-b border-cream/12 py-4">{l}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="font-body font-light text-goldlight/70 text-lg mt-8 max-w-2xl leading-relaxed">
            #VEEDU is Chef Jomon's interpretation of that feeling. A house in Hornchurch becomes a meeting point
            between Kerala and Essex — a place where stories are served alongside food.
          </p>
        </Reveal>
      </div>
    </section>
  );
}



function FoodPhilosophy() {
  return (
    <section className="bg-ink text-goldlight py-12 md:py-16 relative overflow-hidden" data-testid="story-philosophy">
      <div className="u-container grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="u-eyebrow text-terracotta mb-6">Food with a memory</p>
          </Reveal>
          <LineReveal
            lines={["Our food begins", "with Kerala —", "but travels", "beyond it."]}
            lineClassName="font-display text-4xl md:text-6xl leading-[0.98] text-cream"
          />
          <Reveal delay={0.2}>
            <div className="space-y-5 font-body font-light text-goldlight/70 text-lg leading-relaxed max-w-xl mt-8">
              <p>
                We celebrate the richness of Kerala's coastal cuisine — the fire of its chillies, the fragrance of
                curry leaves, the depth of coconut, tamarind and slow cooking. Alongside these sit dishes inspired by
                Delhi, Awadh, Kashmir, Mangalore, Punjab and the Malabar coast.
              </p>
              <p className="font-display italic text-2xl text-gold">
                Every dish should feel like it has somewhere to come from. And somewhere to take you.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6 grid grid-cols-2 gap-4">
          {[FOOD.fishCurry, FOOD.tandoorFire, FOOD.spices, FOOD.scallops].map((src, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className={`overflow-hidden ${i % 2 ? "mt-8" : ""}`}>
                <img src={src} alt="Kerala-inspired dish" loading="lazy" className="w-full aspect-[4/5] object-cover" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}


export default function Story() {
  return (
    <div data-testid="page-story">
      <Opening />
      <ManBehind />
      <WhyVeedu />
      <FoodPhilosophy />
    </div>
  );
}
